// WebSocket hook for real-time orchestrator events
// Connects to orchestrator-alpha WebSocket server for workflow updates

import { useEffect, useRef, useCallback, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { logger } from "../services/logger";
import { generateUUID } from "../utils/uuid";
import {
  calculateBackoff,
  shouldReconnect,
  RECONNECT_CONFIG,
} from "../utils/websocketReconnect";

type ConnectionStatus = "disconnected" | "connecting" | "connected";

interface WebSocketEvent {
  type: "event";
  event: string;
  status: string;
  data: unknown;
  version: string;
  timestamp: string;
}

const PING_INTERVAL_MS = 30000;
const WEBSOCKET_URL = import.meta.env.VITE_WEBSOCKET_URL;

type MessageListener = (message: any) => void;

export function useWebSocket() {
  const { user } = useAuth();
  const wsRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const intentionalCloseRef = useRef(false);
  const reconnectAttemptRef = useRef(0);
  const messageListenersRef = useRef<Set<MessageListener>>(new Set());
  const [status, setStatus] = useState<ConnectionStatus>("disconnected");
  const isConnectingRef = useRef(false);

  // Refs to avoid stale closures in WebSocket event handlers
  const userRef = useRef(user);
  userRef.current = user;
  const connectRef = useRef<() => void>(() => {});

  const connect = useCallback(() => {
    if (!user) {
      logger.warn("WebSocket: No authenticated user", {
        correlationId: generateUUID(),
      });
      return;
    }

    if (!WEBSOCKET_URL) {
      logger.warn("WebSocket: VITE_WEBSOCKET_URL not configured", {
        correlationId: generateUUID(),
      });
      return;
    }

    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      isConnectingRef.current
    ) {
      return;
    }

    isConnectingRef.current = true;
    intentionalCloseRef.current = false;

    const correlationId = generateUUID();
    setStatus("connecting");

    logger.info("WebSocket: Connecting", {
      url: WEBSOCKET_URL,
      correlationId,
    });

    // Note: WebSocket authentication uses the wos-session cookie
    // which is automatically sent by the browser if the WebSocket
    // server is on the same domain or a subdomain

    const ws = new WebSocket(WEBSOCKET_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      isConnectingRef.current = false;
      setStatus("connected");
      reconnectAttemptRef.current = 0;
      logger.info("WebSocket: Connected", {
        correlationId,
      });

      // Clear any lingering ping interval before starting a new one
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, PING_INTERVAL_MS);
    };

    ws.onmessage = (event) => {
      if (wsRef.current !== ws) return;
      try {
        const data = JSON.parse(event.data);

        // Log all received messages to console
        if (data.type === "pong") {
          logger.debug("WebSocket: Pong received", {
            timestamp: data.timestamp,
            correlationId,
          });
        } else if (data.type === "event") {
          const wsEvent = data as WebSocketEvent;
          logger.info("WebSocket: Event received", {
            event: wsEvent.event,
            status: wsEvent.status,
            data: wsEvent.data,
            correlationId,
          });
        } else {
          logger.debug("WebSocket: Message received", {
            type: data.type,
            eventType: data.eventType,
            correlationId,
          });
        }

        // Notify all listeners of the message
        messageListenersRef.current.forEach((listener) => {
          try {
            listener(data);
          } catch (error) {
            logger.error(
              "WebSocket: Error in message listener",
              error as Error,
              {
                correlationId,
              },
            );
          }
        });
      } catch {
        logger.warn("WebSocket: Failed to parse message", {
          raw: event.data,
          correlationId,
        });
      }
    };

    ws.onclose = (event) => {
      // Skip state updates for stale WebSocket (replaced by a newer connection)
      // Note: the new socket's onopen always clears the old ping interval,
      // so there's no leak from skipping cleanup for stale sockets here.
      if (wsRef.current !== ws) return;

      isConnectingRef.current = false;
      setStatus("disconnected");

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      logger.info("WebSocket: Closed", {
        code: event.code,
        reason: event.reason,
        correlationId,
      });

      if (event.code === 4001) {
        logger.warn("WebSocket: Token expired", { correlationId });
      }

      // Auto-reconnect with exponential backoff
      // Use refs to avoid stale closures for user and connect
      const canReconnect = shouldReconnect(
        event.code,
        intentionalCloseRef.current,
      );

      if (
        canReconnect &&
        userRef.current &&
        reconnectAttemptRef.current < RECONNECT_CONFIG.maxAttempts
      ) {
        const delay = calculateBackoff(reconnectAttemptRef.current);
        reconnectAttemptRef.current += 1;

        logger.info("WebSocket: Scheduling reconnect", {
          attempt: reconnectAttemptRef.current,
          delayMs: delay,
          closeCode: event.code,
          correlationId,
        });

        reconnectTimeoutRef.current = setTimeout(() => {
          connectRef.current();
        }, delay);
      } else if (
        canReconnect &&
        reconnectAttemptRef.current >= RECONNECT_CONFIG.maxAttempts
      ) {
        logger.warn("WebSocket: Max reconnect attempts reached", {
          attempts: reconnectAttemptRef.current,
          correlationId,
        });
      }
    };

    ws.onerror = () => {
      if (wsRef.current !== ws) return;
      isConnectingRef.current = false;
      logger.error(
        "WebSocket: Error",
        new Error("WebSocket connection error"),
        {
          correlationId,
        },
      );
    };
  }, [user]);

  // Keep ref in sync so onclose always calls the latest connect
  connectRef.current = connect;

  const disconnect = useCallback(() => {
    intentionalCloseRef.current = true;
    isConnectingRef.current = false;
    reconnectAttemptRef.current = 0;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, "Client disconnect");
      wsRef.current = null;
    }

    setStatus("disconnected");
  }, []);

  const addMessageListener = useCallback((listener: MessageListener) => {
    messageListenersRef.current.add(listener);
    return () => {
      messageListenersRef.current.delete(listener);
    };
  }, []);

  // Connect when user is available, disconnect on unmount
  useEffect(() => {
    if (user) {
      connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return { status, connect, disconnect, addMessageListener };
}
